import { useEffect } from "react";
import useWebSocket from "react-use-websocket";
import { useSnapshot } from "valtio";

import { getInAppCredits } from "../apis/mail-credits/get.in.app.credits";
import { getLovewallSubscription } from "../apis/subscriptions/get.lovewall.subscription";
import EnvConfig from "../config/env.config";
import { cateringStore } from "../pages/Catering/utils";
import { browserNotificationManager } from "../utils/browserNotifications";
import { ClientStorage, ClientStorageItem } from "../utils/clientstorage";
import { lockStore, WeddnAppResource } from "../utils/lock";
import { fetchPackageBundlesAndBookings } from "../utils/package.bundle";
import { subscriptionStore } from "../utils/subscription";
import WSStore, { WSMessage, WSMessageEvent } from "../utils/websocket";
import {
  mailCreditsStore,
  notificationDrawerStore,
  notificationStatusStore,
} from "./store";
import { appBadgeManager } from "../utils/appBadge";
import { refreshSupportUnread, supportStore } from "../utils/support";
import { queryClient } from "../apis/query-client";

type ResourceAction = "acquire" | "release" | "renew";
type ResourceMessage = {
  resource: WeddnAppResource;
  action: ResourceAction;
};
type SystemSignal =
  | "successful_charge"
  | "fetch_email_credits"
  | "successful_credits_purchase"
  | "successful_food_order_created"
  | "successful_package_bundles_purchased"
  | "successful_lovewall_subscription_purchased";
type SystemMessage = {
  signal: SystemSignal;
  signalData: Record<string, unknown>;
};

type NotificationMessage = {
  type: string;
  title: string;
  message: string;
  weddingId: string;
  createdAt: string;
};

const handleWsMessages = <T>(event: WSMessageEvent, data: T) => {
  const XAccountHash =
    ClientStorage.fetch(ClientStorageItem.XAccountHash) || "";
  if (event === "resource") {
    // console.log("handling resource msg", data);
    const resourceData = data as ResourceMessage;
    if (resourceData.action === "acquire" || resourceData.action === "renew") {
      lockStore.setLock(resourceData.resource);
    }

    if (resourceData.action === "release") {
      lockStore.releaseLock(resourceData.resource);
    }
  }

  if (event === "system") {
    // console.log("handling system msg", data);
    const systemData = data as SystemMessage;
    if (systemData.signal === "successful_credits_purchase") {
      const { credits_balance, credits_purchased } = systemData.signalData as {
        credits_balance: number;
        credits_purchased: number;
      };
      mailCreditsStore.inAppCredits = {
        ...mailCreditsStore.inAppCredits,
        balance: credits_balance,
      };
      mailCreditsStore.latestCreditsPurchase = credits_purchased;
    }

    if (systemData.signal === "successful_charge") {
      const { credits_balance } = systemData.signalData as {
        credits_balance: number;
      };
      mailCreditsStore.inAppCredits = {
        ...mailCreditsStore.inAppCredits,
        balance: credits_balance,
      };
    }

    if (systemData.signal === "fetch_email_credits") {
      getInAppCredits({
        headers: {
          "X-Account-Hash": XAccountHash || "",
        },
        onCompleted: (data) => {
          mailCreditsStore.inAppCredits = data.data;
        },
        onError: (err) => {
          console.error(err);
        },
      });
    }

    if (systemData.signal === "successful_lovewall_subscription_purchased") {
      getLovewallSubscription({
        headers: {
          "X-Account-Hash": XAccountHash || "",
        },
        onCompleted(params) {
          subscriptionStore.data = params.data;
        },
        onError(err) {
          console.error(err);
        },
      });
    }

    if (systemData.signal === "successful_food_order_created") {
      cateringStore.orderBundlesToken = Date.now().toString();
    }

    if (systemData.signal === "successful_package_bundles_purchased") {
      fetchPackageBundlesAndBookings();
    }
  }

  if (event === "support" || event === "support_ticket") {
    // Support no longer rides the notification rails. Bump the live tick so an
    // open support view refetches, and refresh the support-icon unread count.
    supportStore.liveTick += 1;
    refreshSupportUnread();
    return;
  }

  if (event === "notification") {
    const notification = data as NotificationMessage;

    // Invalidate guest list and insights queries for real-time updates
    queryClient.invalidateQueries({ queryKey: ["wedding/guest/list"] });
    queryClient.invalidateQueries({ queryKey: ["wedding/guest/all-list"] });
    queryClient.invalidateQueries({ queryKey: ["wedding/insights/guests"] });

    // Update notification count in store
    const newCount = notificationStatusStore.data.unreadCount + 1;
    notificationStatusStore.data = {
      ...notificationStatusStore.data,
      unreadCount: newCount,
    };

    // Update app badge
    appBadgeManager.setBadge(newCount);

    // Show browser notification
    browserNotificationManager.showNotification({
      title: "Weddn",
      body: notification.message,
      tag: `weddn-${notification.type}`,
      onClick: () => {
        // Focus window and open notification drawer
        window.focus();
        notificationDrawerStore.isOpen = true;
      },
    });
  }
};

export const useWebSocketSetup = () => {
  const { websocket } = useSnapshot(WSStore);
  const { sendJsonMessage, lastJsonMessage } = useWebSocket<WSMessage>(
    `${EnvConfig.websocketUrl}/ws`,
    {
      onOpen: () => console.log("WebSocket opened"),
      onClose: () => console.log("WebSocket closed"),
      shouldReconnect: () => true, // Reconnect on disconnection
    }
  );

  useEffect(() => {
    // Only set the WebSocket connection if it doesn't already exist
    if (!websocket) {
      WSStore.websocket = {
        sendJsonMessage,
        lastJsonMessage,
      };
    }

    if (lastJsonMessage) {
      const { event, data } = lastJsonMessage;
      handleWsMessages(event, data);
    }
  }, [websocket, sendJsonMessage, lastJsonMessage]);

  // Seed the support-icon unread badge once on load.
  useEffect(() => {
    refreshSupportUnread();
  }, []);

  const sendMessage = (wsmsg: WSMessage) => {
    sendJsonMessage(wsmsg);
  };

  return { sendMessage };
};
